/**
 * DemocratCharts — multi-chart dashboard state.
 *
 * Holds:
 *   - count: how many panes are visible (1, 2, 4, 6, 8)
 *   - panes: per-pane configs (symbol/exchange/timeframe)
 *   - activeId: which pane has focus (drives trading, replay, sidebar context)
 *
 * Persisted to localStorage so reloading restores the user's exact layout.
 */

import { create } from "zustand";
import { SYMBOLS } from "@/lib/symbols";

export type ChartCount = 1 | 2 | 4 | 6 | 8;
export const VALID_COUNTS: ChartCount[] = [1, 2, 4, 6, 8];

export interface PaneConfig {
  id: string;
  symbol: string;
  exchange: string;
  symbolName: string;
  timeframe: string;
}

const LS_KEY = "livecharts:dashboard:v1";

/** Sensible default symbols to seed new panes. */
const DEFAULT_SEED = ["NIFTY 50", "BANKNIFTY", "RELIANCE", "TCS", "AAPL", "MSFT", "NVDA", "TSLA"];

function defaultPaneAt(index: number): PaneConfig {
  const symbolName = DEFAULT_SEED[index % DEFAULT_SEED.length];
  const target = symbolName.toUpperCase();
  const found =
    SYMBOLS.find((s) => s.name.toUpperCase() === target) ||
    SYMBOLS.find((s) => s.symbol.toUpperCase() === target) ||
    SYMBOLS[0];
  return {
    id: `pane_${index + 1}`,
    symbol: found.symbol,
    exchange: found.exchange,
    symbolName: found.name,
    timeframe: "1d",
  };
}

interface PersistShape {
  count: ChartCount;
  panes: PaneConfig[];
  activeId: string;
}

function load(): PersistShape {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistShape;
      if (
        VALID_COUNTS.includes(parsed.count) &&
        Array.isArray(parsed.panes) &&
        parsed.panes.length === parsed.count &&
        typeof parsed.activeId === "string"
      ) {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }
  const initialPane = defaultPaneAt(0);
  return { count: 1, panes: [initialPane], activeId: initialPane.id };
}

function persist(state: PersistShape) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface DashboardState extends PersistShape {
  setCount: (n: ChartCount) => void;
  setActive: (id: string) => void;
  updatePane: (id: string, patch: Partial<Omit<PaneConfig, "id">>) => void;
  /** Returns the currently-active pane, or the first pane as fallback. */
  getActive: () => PaneConfig;
}

export const useDashboardStore = create<DashboardState>((set, get) => {
  const initial = load();
  return {
    ...initial,

    setCount: (n) => {
      const cur = get();
      let panes = [...cur.panes];
      if (n > panes.length) {
        // Grow — append fresh seeded panes
        for (let i = panes.length; i < n; i++) panes.push(defaultPaneAt(i));
      } else if (n < panes.length) {
        // Shrink — keep first n
        panes = panes.slice(0, n);
      }
      const activeId = panes.some((p) => p.id === cur.activeId) ? cur.activeId : panes[0].id;
      const next = { count: n, panes, activeId };
      persist(next);
      set(next);
    },

    setActive: (id) => {
      const cur = get();
      if (!cur.panes.some((p) => p.id === id)) return;
      const next = { ...cur, activeId: id };
      persist({ count: cur.count, panes: cur.panes, activeId: id });
      set(next);
    },

    updatePane: (id, patch) => {
      const cur = get();
      const panes = cur.panes.map((p) => (p.id === id ? { ...p, ...patch } : p));
      const next = { ...cur, panes };
      persist({ count: cur.count, panes, activeId: cur.activeId });
      set(next);
    },

    getActive: () => {
      const cur = get();
      return cur.panes.find((p) => p.id === cur.activeId) ?? cur.panes[0];
    },
  };
});
